import { useState, useCallback, useMemo } from 'react';
import propertise from '../config/properties';
import toast from 'react-hot-toast';

// Simplified patient search hook without circular dependencies
const useSimplePatientSearch = () => {
  const [searchTerms, setSearchTerms] = useState({
    FirstName: '',
    LastName: '',
    MrnNumber: '',
    MobileNumber: '',
    EmiratesIdNumber: '',
    PassportNumber: '',
    dropdownValue: ''
  });
  
  const [filteredData, setFilteredData] = useState([]);
  const [isFiltered, setIsFiltered] = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [currentData, setCurrentData] = useState([]);

  // Validation rules for search inputs
  const validateSearchInput = useCallback((name, value) => {
    const validationRules = {
      MrnNumber: {
        pattern: /^[A-Za-z0-9-]*$/,
        message: 'MRN can only contain letters, numbers, and hyphens'
      },
      MobileNumber: {
        pattern: /^[+0-9]*$/,
        message: 'Phone Number can only contain numbers and + sign'
      },
      EmiratesIdNumber: {
        pattern: /^[0-9-]*$/,
        message: 'Emirates ID can only contain numbers and hyphens'
      },
      PassportNumber: {
        pattern: /^[A-Za-z0-9]*$/,
        message: 'Passport Number can only contain letters and numbers'
      },
      FirstName: {
        pattern: /^[A-Za-z\s]*$/,
        message: 'First Name can only contain letters and spaces'
      },
      LastName: {
        pattern: /^[A-Za-z\s]*$/,
        message: 'Last Name can only contain letters and spaces'
      }
    };

    const rule = validationRules[name];
    if (rule && value && !rule.pattern.test(value)) {
      return rule.message;
    }
    return null;
  }, []);

  // Handle search input changes with validation
  const handleSearchChange = useCallback((e) => {
    const { name, value } = e.target;
    
    // Validate input
    const validationError = validateSearchInput(name, value);
    if (validationError) {
      setSearchError(validationError);
      toast.error(validationError);
      return;
    }
    
    setSearchError(null);
    setSearchTerms(prev => ({
      ...prev,
      [name]: value
    }));
  }, [validateSearchInput]);

  // Advanced filtering function
  const filterPatients = useCallback((data, searchCriteria) => {
    if (!data || !Array.isArray(data)) return [];

    return data.filter(patient => {
      // Helper function to safely get and normalize field values
      const getFieldValue = (field) => {
        let value = '';
        
        switch (field) {
          case 'FirstName':
            value = patient.firstName || patient[propertise.PatientFirstName] || '';
            break;
          case 'LastName':
            value = patient.lastName || patient[propertise.PatientLastName] || '';
            break;
          case 'MrnNumber':
            value = patient.mrnNumber || patient[propertise.PatientMrnNumber] || '';
            break;
          case 'MobileNumber':
            value = patient.mobileNumber || patient[propertise.PatientMobileNumber] || '';
            break;
          case 'EmiratesIdNumber':
            value = patient.emrNumber || patient[propertise.EMRID] || '';
            break;
          case 'PassportNumber':
            value = patient[propertise.PatientPasportNumber] || '';
            break;
          case 'dropdownValue':
            value = String(patient.status || patient[propertise.PatientSatus] || '');
            break;
          default:
            value = '';
        }
        
        return String(value).trim().toLowerCase();
      };

      // Apply all search criteria
      const matchesFirstName = !searchCriteria.FirstName || 
        getFieldValue('FirstName').includes(searchCriteria.FirstName.trim().toLowerCase());
      
      const matchesLastName = !searchCriteria.LastName || 
        getFieldValue('LastName').includes(searchCriteria.LastName.trim().toLowerCase());
      
      const matchesMrnNumber = !searchCriteria.MrnNumber || 
        getFieldValue('MrnNumber').includes(searchCriteria.MrnNumber.trim().toLowerCase());
      
      const matchesMobileNumber = !searchCriteria.MobileNumber || 
        getFieldValue('MobileNumber').includes(searchCriteria.MobileNumber.trim().toLowerCase());
      
      const matchesEmiratesId = !searchCriteria.EmiratesIdNumber || 
        getFieldValue('EmiratesIdNumber').includes(searchCriteria.EmiratesIdNumber.trim().toLowerCase());
      
      const matchesPassportNumber = !searchCriteria.PassportNumber || 
        getFieldValue('PassportNumber').includes(searchCriteria.PassportNumber.trim().toLowerCase());
      
      const matchesStatus = !searchCriteria.dropdownValue || 
        getFieldValue('dropdownValue').includes(searchCriteria.dropdownValue.trim().toLowerCase());

      return matchesFirstName && matchesLastName && matchesMrnNumber && 
             matchesMobileNumber && matchesEmiratesId && matchesPassportNumber && matchesStatus;
    });
  }, []);

  // Update data (called from context)
  const updateData = useCallback((newData) => {
    setCurrentData(newData || []);
    if (isFiltered && Object.values(searchTerms).some(value => value && String(value).trim() !== '')) {
      const filtered = filterPatients(newData, searchTerms);
      setFilteredData(filtered);
    } else {
      setFilteredData(newData || []);
    }
  }, [isFiltered, searchTerms, filterPatients]);

  // Perform search
  const performSearch = useCallback(() => {
    try {
      setSearchError(null);
      
      // Check if any search criteria is provided
      const hasSearchCriteria = Object.values(searchTerms).some(value => 
        value && String(value).trim() !== ''
      );
      
      if (!hasSearchCriteria) {
        setFilteredData(currentData);
        setIsFiltered(false);
        return { success: true, data: currentData, message: 'Showing all records' };
      }

      const filtered = filterPatients(currentData, searchTerms);
      
      if (filtered.length === 0) {
        toast.error('No patients found matching the search criteria');
        return { success: false, data: [], message: 'No results found' };
      }
      
      setFilteredData(filtered);
      setIsFiltered(true);
      
      const resultMessage = `Found ${filtered.length} patient${filtered.length === 1 ? '' : 's'}`;
      toast.success(resultMessage);
      
      return { success: true, data: filtered, message: resultMessage };
      
    } catch (error) {
      const errorMessage = 'An error occurred while searching. Please try again.';
      setSearchError(errorMessage);
      toast.error(errorMessage);
      
      return { success: false, data: [], message: errorMessage, error };
    }
  }, [searchTerms, filterPatients, currentData]);

  // Reset search
  const resetSearch = useCallback(() => {
    try {
      setSearchTerms({
        FirstName: '',
        LastName: '',
        MrnNumber: '',
        MobileNumber: '',
        EmiratesIdNumber: '',
        PassportNumber: '',
        dropdownValue: ''
      });
      
      setFilteredData(currentData);
      setIsFiltered(false);
      setSearchError(null);
      
      return { success: true, data: currentData, message: 'Search reset successfully' };
    } catch (error) {
      const errorMessage = 'Failed to reset search. Please try again.';
      setSearchError(errorMessage);
      return { success: false, data: [], message: errorMessage, error };
    }
  }, [currentData]);

  // Search summary for display
  const searchSummary = useMemo(() => {
    const hasActiveFilters = Object.values(searchTerms).some(value => 
      value && String(value).trim() !== ''
    );
    
    return {
      hasActiveFilters,
      totalResults: filteredData.length,
      isSearching: isFiltered,
      searchTermsCount: Object.values(searchTerms).filter(value => 
        value && String(value).trim() !== ''
      ).length
    };
  }, [searchTerms, filteredData.length, isFiltered]);

  return useMemo(() => ({
    // State
    searchTerms,
    filteredData,
    isFiltered,
    searchError,
    searchSummary,
    
    // Actions
    handleSearchChange,
    performSearch,
    resetSearch,
    updateData
  }), [
    searchTerms, filteredData, isFiltered, searchError, searchSummary,
    handleSearchChange, performSearch, resetSearch, updateData
  ]);
};

export default useSimplePatientSearch; 